import React, { useState, useMemo } from 'react';

interface TimingEvent {
  player_id: string;
  question_id: string;
  server_question_sent: string;
  client_question_received: string;
  client_answer_sent: string;
  server_answer_received: string;
  server_reveal_sent: string;
  network_lag_ms: number;
  effective_time_ms: number;
}

interface Player {
  id: string;
  name: string;
}

interface MessageFlowVisualizationProps {
  timingEvents: TimingEvent[];
  questionNumber: number;
  questionText: string;
  players: Player[];
}

interface PlayerTimingData {
  playerId: string;
  playerName: string;
  questionDelivery: number; // ms from server_question_sent to client_question_received
  answerSubmission: number; // ms from client_answer_sent to server_answer_received
  fullRoundTrip: number; // ms from server_question_sent to server_answer_received
  timestamps: {
    serverQuestionSent: number;
    clientQuestionReceived: number;
    clientAnswerSent: number;
    serverAnswerReceived: number;
  };
}

const MessageFlowVisualization: React.FC<MessageFlowVisualizationProps> = ({
  timingEvents,
  questionNumber,
  questionText,
  players,
}) => {
  const [hoveredBar, setHoveredBar] = useState<{ playerId: string; phase: string } | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Process timing data for each player
  const playerTimingData = useMemo(() => {
    const data: PlayerTimingData[] = timingEvents.map((event) => {
      const serverQuestionSent = new Date(event.server_question_sent).getTime();
      const clientQuestionReceived = new Date(event.client_question_received).getTime();
      const clientAnswerSent = new Date(event.client_answer_sent).getTime();
      const serverAnswerReceived = new Date(event.server_answer_received).getTime();

      const player = players.find(p => p.id === event.player_id);

      return {
        playerId: event.player_id,
        playerName: player?.name || event.player_id.substring(0, 8),
        questionDelivery: clientQuestionReceived - serverQuestionSent,
        answerSubmission: serverAnswerReceived - clientAnswerSent,
        fullRoundTrip: serverAnswerReceived - serverQuestionSent,
        timestamps: {
          serverQuestionSent,
          clientQuestionReceived,
          clientAnswerSent,
          serverAnswerReceived,
        },
      };
    });

    return data;
  }, [timingEvents, players]);

  // Calculate max values for scaling
  const maxValues = useMemo(() => {
    if (playerTimingData.length === 0) {
      return { questionDelivery: 1000, answerSubmission: 1000, fullRoundTrip: 2000 };
    }

    return {
      questionDelivery: Math.max(...playerTimingData.map(p => p.questionDelivery), 1),
      answerSubmission: Math.max(...playerTimingData.map(p => p.answerSubmission), 1),
      fullRoundTrip: Math.max(...playerTimingData.map(p => p.fullRoundTrip), 1),
    };
  }, [playerTimingData]);

  // Get color based on latency
  const getLatencyColor = (latencyMs: number, phase: 'delivery' | 'submission' | 'roundtrip') => {
    const thresholds = {
      delivery: { good: 500, fair: 1000 },
      submission: { good: 300, fair: 600 },
      roundtrip: { good: 1000, fair: 2000 },
    };

    const threshold = thresholds[phase];

    if (latencyMs < threshold.good) {
      return { bg: 'bg-green-500', text: 'text-green-700', border: 'border-green-300' };
    } else if (latencyMs < threshold.fair) {
      return { bg: 'bg-yellow-500', text: 'text-yellow-700', border: 'border-yellow-300' };
    } else {
      return { bg: 'bg-red-500', text: 'text-red-700', border: 'border-red-300' };
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  };

  const handleMouseEnter = (e: React.MouseEvent, playerId: string, phase: string) => {
    setHoveredBar({ playerId, phase });
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredBar(null);
  };

  if (playerTimingData.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        No timing data available for this question.
      </div>
    );
  }

  // Render a single phase section
  const renderPhase = (
    title: string,
    description: string,
    phaseKey: 'questionDelivery' | 'answerSubmission' | 'fullRoundTrip',
    phaseType: 'delivery' | 'submission' | 'roundtrip'
  ) => {
    const maxValue = maxValues[phaseKey];

    return (
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-xs text-gray-600 mb-3">{description}</p>

        {/* Time axis */}
        <div className="relative mb-2 ml-32 mr-4">
          <div className="flex justify-between text-xs text-gray-500">
            <span>0ms</span>
            <span>{(maxValue * 0.25).toFixed(0)}ms</span>
            <span>{(maxValue * 0.5).toFixed(0)}ms</span>
            <span>{(maxValue * 0.75).toFixed(0)}ms</span>
            <span>{maxValue.toFixed(0)}ms</span>
          </div>
          <div className="h-1 bg-gray-200 rounded mt-1"></div>
        </div>

        {/* Player bars */}
        <div className="space-y-2">
          {playerTimingData.map((playerData) => {
            const value = playerData[phaseKey];
            const percentage = (value / maxValue) * 100;
            const colors = getLatencyColor(value, phaseType);

            return (
              <div key={playerData.playerId} className="flex items-center">
                {/* Player name */}
                <div className="w-28 text-sm text-gray-700 truncate font-medium" title={playerData.playerName}>
                  {playerData.playerName}
                </div>

                {/* Bar container */}
                <div className="flex-1 ml-4 mr-4">
                  <div className="bg-gray-100 rounded-full h-6 relative overflow-visible">
                    <div
                      className={`${colors.bg} h-full rounded-full transition-all duration-300 cursor-pointer hover:opacity-80 flex items-center justify-end pr-2`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                      onMouseEnter={(e) => handleMouseEnter(e, playerData.playerId, phaseKey)}
                      onMouseMove={handleMouseMove}
                      onMouseLeave={handleMouseLeave}
                    >
                      {percentage > 15 && (
                        <span className="text-xs font-semibold text-white">
                          {value.toFixed(0)}ms
                        </span>
                      )}
                    </div>
                    {percentage <= 15 && (
                      <span className={`absolute left-full ml-2 text-xs font-semibold ${colors.text}`}>
                        {value.toFixed(0)}ms
                      </span>
                    )}
                  </div>
                </div>

                {/* Status icon */}
                <div className="w-6 text-center">
                  {value >= (phaseType === 'delivery' ? 1000 : phaseType === 'submission' ? 600 : 2000) && (
                    <span className="text-red-500" title="High latency">⚠️</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mt-4">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-1">
          Message Flow Analysis - Question {questionNumber}
        </h3>
        <p className="text-sm text-gray-600 italic">"{questionText}"</p>
      </div>

      {/* Question Delivery Phase */}
      {renderPhase(
        '📩 Question Delivery (Server → Players)',
        'Time from when server sent the question to when each player received it (downstream latency)',
        'questionDelivery',
        'delivery'
      )}

      {/* Answer Submission Phase */}
      {renderPhase(
        '📤 Answer Submission (Players → Server)',
        'Time from when player sent their answer to when server received it (upstream latency)',
        'answerSubmission',
        'submission'
      )}

      {/* Connection Stability Summary */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-1">🔌 Connection Stability</h4>
        <p className="text-xs text-gray-600 mb-3">Network performance summary for this question</p>

        <div className="grid grid-cols-2 gap-4">
          {/* Average Network Lag */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-blue-600 font-semibold mb-1">Average Network Lag</div>
                <div className="text-2xl font-bold text-blue-700">
                  {(playerTimingData.reduce((sum, p) => sum + p.questionDelivery + p.answerSubmission, 0) / playerTimingData.length).toFixed(0)}ms
                </div>
              </div>
              <div className="text-3xl">📊</div>
            </div>
          </div>

          {/* Players with Issues */}
          <div className={`rounded-lg p-4 border ${
            playerTimingData.filter(p => p.questionDelivery > 1000 || p.answerSubmission > 600).length > 0
              ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200'
              : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-xs font-semibold mb-1 ${
                  playerTimingData.filter(p => p.questionDelivery > 1000 || p.answerSubmission > 600).length > 0
                    ? 'text-yellow-600'
                    : 'text-green-600'
                }`}>
                  Players with High Latency
                </div>
                <div className={`text-2xl font-bold ${
                  playerTimingData.filter(p => p.questionDelivery > 1000 || p.answerSubmission > 600).length > 0
                    ? 'text-yellow-700'
                    : 'text-green-700'
                }`}>
                  {playerTimingData.filter(p => p.questionDelivery > 1000 || p.answerSubmission > 600).length} / {playerTimingData.length}
                </div>
              </div>
              <div className="text-3xl">
                {playerTimingData.filter(p => p.questionDelivery > 1000 || p.answerSubmission > 600).length > 0 ? '⚠️' : '✅'}
              </div>
            </div>
          </div>

          {/* Best Connection */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div>
              <div className="text-xs text-green-600 font-semibold mb-1">Best Connection</div>
              <div className="text-sm font-bold text-green-700 truncate">
                {(() => {
                  const bestPlayer = playerTimingData.reduce((best, current) =>
                    (current.questionDelivery + current.answerSubmission) < (best.questionDelivery + best.answerSubmission)
                      ? current
                      : best
                  );
                  return `${bestPlayer.playerName} (${(bestPlayer.questionDelivery + bestPlayer.answerSubmission).toFixed(0)}ms)`;
                })()}
              </div>
            </div>
          </div>

          {/* Worst Connection */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
            <div>
              <div className="text-xs text-red-600 font-semibold mb-1">Slowest Connection</div>
              <div className="text-sm font-bold text-red-700 truncate">
                {(() => {
                  const worstPlayer = playerTimingData.reduce((worst, current) =>
                    (current.questionDelivery + current.answerSubmission) > (worst.questionDelivery + worst.answerSubmission)
                      ? current
                      : worst
                  );
                  return `${worstPlayer.playerName} (${(worstPlayer.questionDelivery + worstPlayer.answerSubmission).toFixed(0)}ms)`;
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-8 h-3 bg-green-500 rounded"></div>
            <span className="text-gray-600">Good latency</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-3 bg-yellow-500 rounded"></div>
            <span className="text-gray-600">Fair latency</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-3 bg-red-500 rounded"></div>
            <span className="text-gray-600">Poor latency</span>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredBar && (() => {
        const playerData = playerTimingData.find(p => p.playerId === hoveredBar.playerId);
        if (!playerData) return null;

        return (
          <div
            className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 pointer-events-none"
            style={{
              left: `${mousePosition.x + 10}px`,
              top: `${mousePosition.y + 10}px`,
            }}
          >
            <div className="font-bold mb-2">{playerData.playerName}</div>
            <div className="space-y-1">
              {hoveredBar.phase === 'questionDelivery' && (
                <>
                  <div>Delivery Time: <span className="font-semibold">{playerData.questionDelivery.toFixed(0)}ms</span></div>
                  <div className="text-gray-400 text-xs mt-2">
                    <div>Server sent: {formatTimestamp(playerData.timestamps.serverQuestionSent)}</div>
                    <div>Client received: {formatTimestamp(playerData.timestamps.clientQuestionReceived)}</div>
                  </div>
                </>
              )}
              {hoveredBar.phase === 'answerSubmission' && (
                <>
                  <div>Submission Time: <span className="font-semibold">{playerData.answerSubmission.toFixed(0)}ms</span></div>
                  <div className="text-gray-400 text-xs mt-2">
                    <div>Client sent: {formatTimestamp(playerData.timestamps.clientAnswerSent)}</div>
                    <div>Server received: {formatTimestamp(playerData.timestamps.serverAnswerReceived)}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MessageFlowVisualization;
